import { instanceOfErrorListResponse, ErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../../hooks/useToastCreator';
import { AssetSelectedTypeMap } from '../../../../../../../store/product/types';
import { fundsWithdrawDateQuotation } from '../../../../../../../store/redemptionSolicitation/selector';
import { FundWithdrawDateQuotationNormalized } from '../../../../../../../store/redemptionSolicitation/types';
import { setDateTimeToZero } from '../../../../../../../utils/setDateTimeToZero';

export type DateErrorMessage = { message: string };

interface FundsSellQuotationControllerProps {
  isTotalRedemption: boolean;
  accountId: number;
  product: AssetSelectedTypeMap['fundsSell'];
}

export const useFundsSellQuotationController = ({
  isTotalRedemption,
  accountId,
  product,
}: FundsSellQuotationControllerProps) => {
  const { showToastFromError } = useToastCreator('date-picker-redemption-fund-toast');

  const [selectedDate, setSelectedDate] = useState(setDateTimeToZero(new Date()));
  const [quotationResponse, setQuotationResponse] = useState<FundWithdrawDateQuotationNormalized>();

  const [requestDateErrorMessage, setRequestDateErrorMessage] = useState<DateErrorMessage>();

  const { data, loading, error } = useRecoilQuery(
    fundsWithdrawDateQuotation({
      date: selectedDate,
      totalValueOption: isTotalRedemption,
      productId: Number(product.productId),
      accountId,
    }),
    {
      if: () => !Utils.Date.IsPast(selectedDate) && Utils.Date.IsWeekday(selectedDate),
    }
  );

  const normalizeRequestDate = (date: Date): Date => {
    return setDateTimeToZero(date);
  };

  const onChangeRequestDate = (date: Date) => {
    const normalizedDate = normalizeRequestDate(date);

    const isPast = Utils.Date.IsPast(normalizedDate);
    const isWeekend = Utils.Date.IsWeekend(normalizedDate);

    if (isPast || isWeekend) {
      setRequestDateErrorMessage({ message: 'Adicione uma data válida.' });
    } else {
      setRequestDateErrorMessage(undefined);
      setSelectedDate(date);
      setQuotationResponse(undefined);
    }
  };

  const onResetRequestDate = () => {
    const currentDate = normalizeRequestDate(new Date());
    setSelectedDate(currentDate);
    setQuotationResponse(undefined);
  };

  const validateRequestDate = (date: Date) => {
    const isPast = Utils.Date.IsPast(date);
    const isWeekend = Utils.Date.IsWeekend(date);

    if (isPast || isWeekend) {
      setRequestDateErrorMessage({ message: 'Adicione uma data válida.' });
    } else {
      setRequestDateErrorMessage(undefined);
      setSelectedDate(date);
    }
  };

  useEffect(() => {
    if (error) {
      if (instanceOfErrorListResponse(error)) {
        (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          showToastFromError(errorItem)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!loading && data?.liquidityDateFormatted && data?.quotationDateFormatted) {
      setQuotationResponse(data);
    }
  }, [loading, data]);

  useEffect(() => {
    validateRequestDate(selectedDate);
  }, [selectedDate]);

  return {
    requestDateErrorMessage,
    onChangeRequestDate,
    onResetRequestDate,
    selectedDate,
    isLoadingDates: loading,
    quotationResponse,
  };
};
